import React, { useState, useEffect, useRef } from 'react';
import { fetchBookings, updateBooking, fetchDoctorRecentBookings, fetchProntuarioConfig, updateProntuarioConfig, transcribeAudio } from '../../api';
import PacientSearchSelect from '../../PacientSearchSelect/PacientSearchSelect';
import { ClipLoader } from 'react-spinners';
import TemplatesModal from '../Modals/TemplatesModal';
import { FaSave, FaPrint, FaCalendarAlt, FaPencilAlt, FaMicrophone, FaStop, FaPlus} from 'react-icons/fa';
import { Auth } from 'aws-amplify';

const TRANSCRIPTION_CHUNK_MS = 5000;
const MAX_TRANSCRIPTION_CHUNK_MS = 10000;
const SILENCE_HOLD_MS = 450;
const SILENCE_RMS_THRESHOLD = 0.015;
const LOCAL_DRAFTS_KEY = 'prontuario_receita_local_drafts';

const readLocalDrafts = () => {
  try {
    const drafts = localStorage.getItem(LOCAL_DRAFTS_KEY);
    return drafts ? JSON.parse(drafts) : {};
  } catch (error) {
    console.error('Error reading prontuario local drafts:', error);
    return {};
  }
};

const getLocalDraft = (bookingId) => {
  if (!bookingId) return null;
  return readLocalDrafts()[bookingId] || null;
};

const saveLocalDraft = (bookingId, values) => {
  if (!bookingId) return;

  try {
    const drafts = readLocalDrafts();
    drafts[bookingId] = {
      medicalRecord: values.medicalRecord,
      prescription: values.prescription,
      updatedAt: new Date().toISOString()
    };
    localStorage.setItem(LOCAL_DRAFTS_KEY, JSON.stringify(drafts));
  } catch (error) {
    console.error('Error saving prontuario local draft:', error);
  }
};

const removeLocalDraft = (bookingId) => {
  if (!bookingId) return;

  try {
    const drafts = readLocalDrafts();
    if (!drafts[bookingId]) return;
    delete drafts[bookingId];
    localStorage.setItem(LOCAL_DRAFTS_KEY, JSON.stringify(drafts));
  } catch (error) {
    console.error('Error removing prontuario local draft:', error);
  }
};

const flattenAudioChunks = (chunks) => {
  const totalLength = chunks.reduce((total, chunk) => total + chunk.length, 0);
  const result = new Float32Array(totalLength);
  let offset = 0;

  chunks.forEach(chunk => {
    result.set(chunk, offset);
    offset += chunk.length;
  });

  return result;
};

const encodeWav = (samples, sampleRate) => {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  const writeString = (offset, value) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i += 1, offset += 2) {
    const sample = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }

  return new Blob([view], { type: 'audio/wav' });
};

const calculateRms = (samples) => {
  if (!samples.length) return 0;

  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    sum += samples[i] * samples[i];
  }

  return Math.sqrt(sum / samples.length);
};

function ViewProntuario() {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientBookings, setPatientBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [medicalRecord, setMedicalRecord] = useState('');
  const [prescription, setPrescription] = useState('');
  const [prescriptionHistory, setPrescriptionHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ message: '', type: '' });
  const [medicalTemplates, setMedicalTemplates] = useState([

    
  ]);
  const [prescriptionTemplates, setPrescriptionTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [newType, setNewType] = useState('medical');
  const [newLabel, setNewLabel] = useState('');
  const [newText, setNewText] = useState('');
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editText, setEditText] = useState('');
  const [recordingField, setRecordingField] = useState(null);
  const [transcribingField, setTranscribingField] = useState(null);
  const recorderFieldRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const processorNodeRef = useRef(null);
  const audioChunksRef = useRef([]);
  const chunkStartedAtRef = useRef(0);
  const lastSoundAtRef = useRef(0);
  const transcriptionQueueRef = useRef([]);
  const processingTranscriptionRef = useRef(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState({ message: '', type: '' });
  const latestRecordValuesRef = useRef({ medicalRecord: '', prescription: '' });
  const selectedBookingRef = useRef(null);
  const lastSavedRecordRef = useRef({ bookingId: null, medicalRecord: '', prescription: '' });
  const autoSaveInFlightRef = useRef(false);
  const autoSaveStatusTimeoutRef = useRef(null);

  useEffect(() => {
    latestRecordValuesRef.current = { medicalRecord, prescription };
  }, [medicalRecord, prescription]);

  useEffect(() => {
    selectedBookingRef.current = selectedBooking;
  }, [selectedBooking]);

  useEffect(() => {
    if (!selectedBooking) return;

    const bookingId = selectedBooking.atendimento_id;
    const lastSaved = lastSavedRecordRef.current;
    const hasUnsavedChanges = lastSaved.bookingId === bookingId
      && (lastSaved.medicalRecord !== medicalRecord || lastSaved.prescription !== prescription);

    if (hasUnsavedChanges) {
      saveLocalDraft(bookingId, { medicalRecord, prescription });
    }
  }, [medicalRecord, prescription, selectedBooking]);

  const updateSavedBookingState = (bookingId, values) => {
    setPatientBookings(prevBookings => prevBookings.map(booking => {
      if (booking.atendimento_id === bookingId) {
        const updatedBooking = {
          ...booking,
          medical_record: values.medicalRecord,
          prescription: values.prescription
        };
        if (values.prescriptionHistory) {
          updatedBooking.prescription_history = values.prescriptionHistory;
        }
        return updatedBooking;
      }
      return booking;
    }));

    setSelectedBooking(prevBooking => {
      if (!prevBooking || prevBooking.atendimento_id !== bookingId) {
        return prevBooking;
      }

      const updatedBooking = {
        ...prevBooking,
        medical_record: values.medicalRecord,
        prescription: values.prescription
      };
      if (values.prescriptionHistory) {
        updatedBooking.prescription_history = values.prescriptionHistory;
      }
      return updatedBooking;
    });
  };

  const scheduleAutoSaveStatusClear = (message, type) => {
    if (autoSaveStatusTimeoutRef.current) {
      clearTimeout(autoSaveStatusTimeoutRef.current);
    }

    setAutoSaveStatus({ message, type });
    autoSaveStatusTimeoutRef.current = setTimeout(() => {
      setAutoSaveStatus({ message: '', type: '' });
    }, 5000);
  };

  const appendTranscription = (field, transcription) => {
    const setValue = field === 'medical' ? setMedicalRecord : setPrescription;

    setValue(prev => {
      if (!prev) return transcription;
      const separator = prev.endsWith('\n') ? '\n' : '\n\n';
      return `${prev}${separator}${transcription}`;
    });
  };

  const queueTranscriptionChunk = (field, chunks, sampleRate) => {
    if (!chunks.length) {
      return;
    }

    transcriptionQueueRef.current.push({
      field,
      samples: flattenAudioChunks(chunks),
      sampleRate
    });
    processTranscriptionQueue();
  };

  const flushCurrentAudioChunk = () => {
    const chunks = audioChunksRef.current;
    const audioContext = audioContextRef.current;
    const field = recorderFieldRef.current;

    if (!chunks.length || !audioContext || !field) {
      return;
    }

    queueTranscriptionChunk(field, chunks, audioContext.sampleRate);
    audioChunksRef.current = [];
    chunkStartedAtRef.current = Date.now();
  };

  const processTranscriptionQueue = async () => {
    if (processingTranscriptionRef.current) {
      return;
    }

    processingTranscriptionRef.current = true;

    try {
      while (transcriptionQueueRef.current.length > 0) {
        const item = transcriptionQueueRef.current.shift();
        setTranscribingField(item.field);
        setSaveStatus({ message: 'Transcrevendo trecho de áudio...', type: 'success' });

        const wavBlob = encodeWav(item.samples, item.sampleRate);
        const audioFile = new File([wavBlob], 'prontuario-recording.wav', {
          type: 'audio/wav',
          lastModified: Date.now()
        });
        const transcription = await transcribeAudio(audioFile);

        if (transcription) {
          appendTranscription(item.field, transcription);
        }
      }

      if (!recorderFieldRef.current) {
        setTranscribingField(null);
        setSaveStatus({ message: 'Transcrição inserida.', type: 'success' });
      }
    } catch (error) {
      console.error('Error transcribing recording chunk:', error);
      setSaveStatus({
        message: error.message || 'Erro ao transcrever o áudio.',
        type: 'error'
      });
      setTranscribingField(null);
    } finally {
      processingTranscriptionRef.current = false;

      if (transcriptionQueueRef.current.length > 0) {
        processTranscriptionQueue();
      }
    }
  };

  const stopAudioCapture = async () => {
    if (processorNodeRef.current) {
      processorNodeRef.current.disconnect();
      processorNodeRef.current.onaudioprocess = null;
      processorNodeRef.current = null;
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }

    if (audioContextRef.current) {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }
  };

  useEffect(() => {
    const intervalId = setInterval(async () => {
      const booking = selectedBookingRef.current;
      if (!booking || autoSaveInFlightRef.current) return;

      const bookingId = booking.atendimento_id;
      const currentValues = latestRecordValuesRef.current;
      const lastSaved = lastSavedRecordRef.current;
      const hasChanges = lastSaved.bookingId !== bookingId
        || lastSaved.medicalRecord !== currentValues.medicalRecord
        || lastSaved.prescription !== currentValues.prescription;

      if (!hasChanges) return;

      const valuesToSave = { ...currentValues };
      autoSaveInFlightRef.current = true;
      setAutoSaveStatus({ message: 'Salvando automaticamente...', type: 'saving' });

      try {
        await updateBooking({
          atendimento_id: bookingId,
          medical_record: valuesToSave.medicalRecord,
          prescription: valuesToSave.prescription
        });

        lastSavedRecordRef.current = {
          bookingId,
          medicalRecord: valuesToSave.medicalRecord,
          prescription: valuesToSave.prescription
        };
        removeLocalDraft(bookingId);
        updateSavedBookingState(bookingId, valuesToSave);
        scheduleAutoSaveStatusClear('Salvo no sistema recentemente.', 'success');
      } catch (error) {
        console.error('Error autosaving medical record:', error);
        saveLocalDraft(bookingId, valuesToSave);
        scheduleAutoSaveStatusClear('Não foi possível salvar no sistema. Alterações salvas neste dispositivo.', 'error');
      } finally {
        autoSaveInFlightRef.current = false;
      }
    }, 4000);

    return () => {
      clearInterval(intervalId);
      if (autoSaveStatusTimeoutRef.current) {
        clearTimeout(autoSaveStatusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    return () => {
      stopAudioCapture();
    };
  }, []);

  const startRecording = async (field) => {
    try {
      setSaveStatus({ message: '', type: '' });

      if (recordingField || transcribingField) {
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microfone não suportado neste navegador.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      const sourceNode = audioContext.createMediaStreamSource(stream);
      const processorNode = audioContext.createScriptProcessor(4096, 1, 1);
      const now = Date.now();

      recorderFieldRef.current = field;
      mediaStreamRef.current = stream;
      audioContextRef.current = audioContext;
      sourceNodeRef.current = sourceNode;
      processorNodeRef.current = processorNode;
      audioChunksRef.current = [];
      transcriptionQueueRef.current = [];
      chunkStartedAtRef.current = now;
      lastSoundAtRef.current = now;

      processorNode.onaudioprocess = (event) => {
        const input = event.inputBuffer.getChannelData(0);
        const samples = new Float32Array(input);
        const timestamp = Date.now();
        const rms = calculateRms(samples);

        audioChunksRef.current.push(samples);

        if (rms >= SILENCE_RMS_THRESHOLD) {
          lastSoundAtRef.current = timestamp;
        }

        const chunkAge = timestamp - chunkStartedAtRef.current;
        const silenceAge = timestamp - lastSoundAtRef.current;
        const shouldFlushAtPause = chunkAge >= TRANSCRIPTION_CHUNK_MS && silenceAge >= SILENCE_HOLD_MS;
        const shouldFlushAtLimit = chunkAge >= MAX_TRANSCRIPTION_CHUNK_MS;

        if (shouldFlushAtPause || shouldFlushAtLimit) {
          flushCurrentAudioChunk();
        }
      };

      sourceNode.connect(processorNode);
      processorNode.connect(audioContext.destination);
      setRecordingField(field);
    } catch (error) {
      console.error('Error starting recording:', error);
      setSaveStatus({
        message: 'Não foi possível iniciar a gravação. Verifique a permissão do microfone.',
        type: 'error'
      });
    }
  };

  const stopRecording = async (field) => {
    const activeField = recorderFieldRef.current || field;

    try {
      setRecordingField(null);
      setTranscribingField(activeField);
      setSaveStatus({ message: 'Finalizando transcrição...', type: 'success' });
      flushCurrentAudioChunk();
      await stopAudioCapture();
      recorderFieldRef.current = null;
      processTranscriptionQueue();
    } catch (error) {
      console.error('Error stopping recording:', error);
      setSaveStatus({
        message: error.message || 'Erro ao encerrar a gravação. Tente novamente.',
        type: 'error'
      });
    } finally {
      recorderFieldRef.current = null;
      setRecordingField(null);
    }
  };

  const renderTranscriptionButton = (field) => {
    const isRecording = recordingField === field;
    const isTranscribing = transcribingField === field;
    const isBusyElsewhere = (recordingField && recordingField !== field) || (transcribingField && transcribingField !== field);

    return (
      <button
        type="button"
        className={`transcribe-button ${isRecording ? 'recording' : ''}`}
        onClick={() => isRecording ? stopRecording(field) : startRecording(field)}
        disabled={loading || (!isRecording && isTranscribing) || isBusyElsewhere}
      >
        {isRecording ? (
          <><FaStop /> Parar e transcrever</>
        ) : isTranscribing ? (
          <><ClipLoader color="#ffffff" size={13} /> Transcrevendo...</>
        ) : (
          <><FaMicrophone /> Gravar áudio</>
        )}
      </button>
    );
  };

  const handleMedicalTemplateSelect = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;
    const template = medicalTemplates.find(t => t.value === selectedValue);
    if (!template) return;
    setMedicalRecord(prev => {
      if (!prev) return template.text;
      const needsSpacing = prev && !prev.endsWith('\n');
      const separator = needsSpacing ? '\n\n' : '\n';
      return prev + separator + template.text;
    });
    e.target.value = '';
  };

  const handlePrescriptionTemplateSelect = (e) => {
    const selectedValue = e.target.value;
    if (!selectedValue) return;
    const template = prescriptionTemplates.find(t => t.value === selectedValue);
    if (!template) return;
    setPrescription(prev => {
      if (!prev) return template.text;
      const needsSpacing = prev && !prev.endsWith('\n');
      const separator = needsSpacing ? '\n\n' : '\n';
      return prev + separator + template.text;
    });
    e.target.value = '';
  };

  useEffect(() => {
    loadDoctorRecentBookings();
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setTemplatesLoading(true);
        const cfg = await fetchProntuarioConfig();
        if (cfg && Array.isArray(cfg.medical_templates) && cfg.medical_templates.length > 0) {
          setMedicalTemplates(cfg.medical_templates);
        }
        if (cfg && Array.isArray(cfg.prescription_templates) && cfg.prescription_templates.length > 0) {
          setPrescriptionTemplates(cfg.prescription_templates);
        }
      } finally {
        setTemplatesLoading(false);
      }
    };
    load();
  }, []);

  const loadDoctorRecentBookings = async () => {
    try {
      setBookingsLoading(true);
      const currentUser = await Auth.currentAuthenticatedUser();
      const session = currentUser.signInUserSession;
      
      if (session && session.idToken && session.idToken.payload) {
        const payload = session.idToken.payload;
        if (payload['custom:doctor_id']) {
          const doctorId = payload['custom:doctor_id'];
          const bookings = await fetchDoctorRecentBookings(doctorId);
          setPatientBookings(bookings);
        }
      }
    } catch (error) {
      console.error('Error loading doctor bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handlePatientSelect = async (patient) => {
    setSelectedPatient(patient);
    if (patient && patient.pacientId) {
      await fetchPatientBookings(patient.pacientId);
      setPatientInfo(patient);
      setSelectedBooking(null);
      setMedicalRecord('');
      setPrescription('');
      setPrescriptionHistory([]);
      lastSavedRecordRef.current = { bookingId: null, medicalRecord: '', prescription: '' };
      setAutoSaveStatus({ message: '', type: '' });
      setSaveStatus({ message: '', type: '' });
    }
  };

  const fetchPatientBookings = async (patientId) => {
    try {
      setBookingsLoading(true);
      const today = new Date().toISOString().split('T')[0];
      
      const queryParams = {
        patientId: patientId,
        endDate: today
      };
      
      const bookingsData = await fetchBookings(queryParams);
      
      // Sort bookings by date descending (newest first)
      const sortedBookings = bookingsData
        .sort((a, b) => new Date(b.datetimeAtendimento) - new Date(a.datetimeAtendimento));
      
      setPatientBookings(sortedBookings);
    } catch (error) {
      console.error('Error fetching patient bookings:', error);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleBookingSelect = (booking) => {
    const currentMedicalRecord = booking.medical_record || '';
    const currentPrescription = booking.prescription || '';
    const currentPrescriptionHistory = Array.isArray(booking.prescription_history)
      ? [...booking.prescription_history].sort((a, b) => new Date(b.created_at || b.printed_at || 0) - new Date(a.created_at || a.printed_at || 0))
      : [];
    const localDraft = getLocalDraft(booking.atendimento_id);
    const hasLocalDraft = localDraft
      && (localDraft.medicalRecord !== currentMedicalRecord || localDraft.prescription !== currentPrescription);
    const nextMedicalRecord = hasLocalDraft ? localDraft.medicalRecord : currentMedicalRecord;
    const nextPrescription = hasLocalDraft ? localDraft.prescription : currentPrescription;

    setSelectedBooking(booking);
    setSaveStatus({ message: '', type: '' });
    setMedicalRecord(nextMedicalRecord);
    setPrescription(nextPrescription);
    setPrescriptionHistory(currentPrescriptionHistory);
    lastSavedRecordRef.current = {
      bookingId: booking.atendimento_id,
      medicalRecord: currentMedicalRecord,
      prescription: currentPrescription
    };
    if (hasLocalDraft) {
      setAutoSaveStatus({
        message: 'Rascunho local recuperado. Clique em Salvar para enviar ao sistema.',
        type: 'warning'
      });
    } else {
      removeLocalDraft(booking.atendimento_id);
      setAutoSaveStatus({ message: '', type: '' });
    }
    
    // If booking has patientData, set it as the selected patient
    if (booking.patientData && !selectedPatient) {
      setSelectedPatient(booking.patientData);
      setPatientInfo(booking.patientData);
    }
  };

  const saveChanges = async () => {
    if (!selectedBooking) return;
    
    try {
      setLoading(true);
      setSaveStatus({ message: '', type: '' });
      
      const updates = {
        atendimento_id: selectedBooking.atendimento_id,
        medical_record: medicalRecord,
        prescription: prescription
      };
      
      await updateBooking(updates);
      lastSavedRecordRef.current = {
        bookingId: selectedBooking.atendimento_id,
        medicalRecord,
        prescription
      };
      removeLocalDraft(selectedBooking.atendimento_id);
      setSaveStatus({ 
        message: 'Prontuário e receita salvos no sistema.', 
        type: 'success' 
      });
      
      // Update the local bookings data with the new values
      updateSavedBookingState(selectedBooking.atendimento_id, { medicalRecord, prescription });
    } catch (error) {
      console.error('Error saving medical record:', error);
      saveLocalDraft(selectedBooking.atendimento_id, { medicalRecord, prescription });
      setSaveStatus({ 
        message: 'Não foi possível salvar no sistema. Seu prontuário e receita ficaram salvos neste dispositivo.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
      setTimeout(() => setSaveStatus({ message: '', type: '' }), 5000);
    }
  };

  // Helper function to format medical text (Prontuario and Receita) with markdown-like syntax and variable substitution
  const formatMedicalText = (text) => {
    if (!text) return '';

    const patientName = patientInfo?.Nome || '';
    const currentDate = new Date().toLocaleDateString('pt-BR');

    // Process line by line
    const lines = text.split('\n');
    const formattedLines = lines.map(line => {
      // Replace Paciente: and Data: placeholders
      let processedLine = line
        .replace(/Paciente:\s*/gi, `Paciente: ${patientName} `)
        .replace(/Data:\s*/gi, `Data: ${currentDate} `);

      // Convert markdown headers to HTML
      if (processedLine.startsWith('#### ')) {
        return `<h4>${processedLine.substring(4)}</h4>`;
      } else if (processedLine.startsWith('### ')) {
        return `<h3>${processedLine.substring(3)}</h3>`;
      } else if (processedLine.startsWith('## ')) {
        return `<h2>${processedLine.substring(2)}</h2>`;
      } else if (processedLine.startsWith('# ')) {
        return `<h1>${processedLine.substring(1)}</h1>`;
      }

      return processedLine;
    });

    // Join lines, but don't add <br> after headers
    let result = '';
    for (let i = 0; i < formattedLines.length; i++) {
      const line = formattedLines[i];
      result += line;
      // Don't add <br> after headers or if it's the last line
      if (i < formattedLines.length - 1 && !line.startsWith('<h')) {
        result += '<br>';
      }
    }

    return result;
  };

  const writePrescriptionPrintWindow = (printWindow, content) => {
    if (!printWindow) return;

    printWindow.document.write(`
      <html>
        <head>
          <title> </title>
          <style>
            @page {
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 150px 20px 20px 20px;
            }
            h1 {
              text-align: center;
              margin-bottom: 30px;
            }
            h2, h3, h4 {
              margin: 15px 0 10px 0;
              color: #333;
            }
            h2 { font-size: 1.4em; }
            h3 { font-size: 1.2em; }
            h4 { font-size: 1.1em; }
            .patient-info {
              margin-bottom: 30px;
              padding-bottom: 10px;
              border-bottom: 1px solid #ddd;
            }
            .prescription {
              font-size: 14px;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-style: italic;
            }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <button onclick="window.print()">Imprimir</button>
          <div class="prescription">
            ${formatMedicalText(content)}
          </div>
          <div class="footer">
            <p>____________________________</p>
            <p>${selectedBooking?.professional || selectedBooking?.profissional || 'Médico'}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrint = async () => {
    if (!prescription) {
      alert('Não há receita para imprimir.');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Não foi possível abrir a janela de impressão.');
      return;
    }

    printWindow.document.write('<html><body><p>Preparando receita...</p></body></html>');
    printWindow.document.close();

    const contentToSave = prescription;
    autoSaveInFlightRef.current = true;

    try {
      setLoading(true);
      setSaveStatus({ message: '', type: '' });

      const result = await updateBooking({
        atendimento_id: selectedBooking.atendimento_id,
        medical_record: medicalRecord,
        prescription: contentToSave,
        save_prescription_snapshot: true,
        prescription_snapshot_content: contentToSave,
        prescription_snapshot_source: 'print'
      });

      const savedSnapshot = result.prescription_snapshot || {
        id: `${selectedBooking.atendimento_id}-${Date.now()}`,
        content: contentToSave,
        created_at: new Date().toISOString(),
        printed_at: new Date().toISOString(),
        read_only: true
      };
      const nextHistory = [savedSnapshot, ...prescriptionHistory];

      setPrescriptionHistory(nextHistory);
      lastSavedRecordRef.current = {
        bookingId: selectedBooking.atendimento_id,
        medicalRecord,
        prescription: contentToSave
      };
      removeLocalDraft(selectedBooking.atendimento_id);
      updateSavedBookingState(selectedBooking.atendimento_id, {
        medicalRecord,
        prescription: contentToSave,
        prescriptionHistory: nextHistory
      });
      setSaveStatus({
        message: 'Receita salva no histórico e pronta para impressão.',
        type: 'success'
      });

      printWindow.document.open();
      writePrescriptionPrintWindow(printWindow, savedSnapshot.content);
    } catch (error) {
      console.error('Error saving prescription snapshot:', error);
      printWindow.close();
      saveLocalDraft(selectedBooking.atendimento_id, { medicalRecord, prescription: contentToSave });
      setSaveStatus({
        message: 'Não foi possível salvar a receita no histórico. Impressão cancelada.',
        type: 'error'
      });
    } finally {
      autoSaveInFlightRef.current = false;
      setLoading(false);
      setTimeout(() => setSaveStatus({ message: '', type: '' }), 5000);
    }
  };

  const handleReprintPrescription = (snapshot) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Não foi possível abrir a janela de impressão.');
      return;
    }
    writePrescriptionPrintWindow(printWindow, snapshot.content || '');
  };

  const handleNewPrescription = () => {
    setPrescription('');
  };

  const handlePrintProntuario = () => {
    if (!medicalRecord) {
      alert('Não há prontuário para imprimir.');
      return;
    }
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title> </title>
          <style>
            @page {
              margin: 0;
            }
            body {
              font-family: Arial, sans-serif;
              line-height: 1.6;
              margin: 150px 20px 20px 20px;
            }
            h1 {
              text-align: center;
              margin-bottom: 30px;
            }
            h2, h3, h4 {
              margin: 15px 0 10px 0;
              color: #333;
            }
            h2 { font-size: 1.4em; }
            h3 { font-size: 1.2em; }
            h4 { font-size: 1.1em; }
            .patient-info {
              margin-bottom: 30px;
              padding-bottom: 10px;
              border-bottom: 1px solid #ddd;
            }
            .prescription {
              font-size: 14px;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-style: italic;
            }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <button onclick="window.print()">Imprimir</button>
          <div class="prescription">
            ${formatMedicalText(medicalRecord)}
          </div>
          <div class="footer">
            <p>____________________________</p>
            <p>${selectedBooking?.professional || 'Médico'}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const saveTemplates = async (newMedical, newPrescription) => {
    await updateProntuarioConfig({
      medical_templates: newMedical,
      prescription_templates: newPrescription
    });
    setMedicalTemplates(newMedical);
    setPrescriptionTemplates(newPrescription);
  };

  const addTemplate = async () => {
    if (!newLabel || !newText) return;
    const slug = newLabel.toLowerCase().trim().replace(/\s+/g, '-');
    const tmpl = { value: slug, label: newLabel, text: newText };
    if (newType === 'medical') {
      await saveTemplates([...medicalTemplates, tmpl], prescriptionTemplates);
    } else {
      await saveTemplates(medicalTemplates, [...prescriptionTemplates, tmpl]);
    }
    setNewLabel('');
    setNewText('');
  };

  const deleteTemplate = async (type, index) => {
    if (type === 'medical') {
      const arr = medicalTemplates.filter((_, i) => i !== index);
      await saveTemplates(arr, prescriptionTemplates);
    } else {
      const arr = prescriptionTemplates.filter((_, i) => i !== index);
      await saveTemplates(medicalTemplates, arr);
    }
  };

  const startEdit = (type, index) => {
    setEditing({ type, index });
    if (type === 'medical') {
      setEditLabel(medicalTemplates[index].label);
      setEditText(medicalTemplates[index].text);
    } else {
      setEditLabel(prescriptionTemplates[index].label);
      setEditText(prescriptionTemplates[index].text);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    const slug = editLabel.toLowerCase().trim().replace(/\s+/g, '-');
    if (editing.type === 'medical') {
      const arr = medicalTemplates.map((t, i) => i === editing.index ? { value: slug, label: editLabel, text: editText } : t);
      await saveTemplates(arr, prescriptionTemplates);
    } else {
      const arr = prescriptionTemplates.map((t, i) => i === editing.index ? { value: slug, label: editLabel, text: editText } : t);
      await saveTemplates(medicalTemplates, arr);
    }
    setEditing(null);
    setEditLabel('');
    setEditText('');
  };

  return (
    <div className="prontuario-content">
      <div className="prontuario-form">
        <div className="patient-selection">
          <label>Selecione o Paciente:</label>
          <PacientSearchSelect 
            onPatientSelect={handlePatientSelect} 
          />
        </div>

        {selectedPatient && (
          <div className="patient-info">
            <h3>Informações do Paciente</h3>
            <div className="patient-info-grid">
              <div className="patient-info-item">
                <label>Nome</label>
                <span>{patientInfo?.Nome}</span>
              </div>
              <div className="patient-info-item">
                <label>CPF</label>
                <span>{patientInfo?.CPF}</span>
              </div>
              <div className="patient-info-item">
                <label>Data de Nascimento</label>
                <span>{patientInfo?.Aniversario}</span>
              </div>
              <div className="patient-info-item">
                <label>Telefone</label>
                <span>{patientInfo?.Fone}</span>
              </div>
              <div className="patient-info-item">
                <label>Email</label>
                <span>{patientInfo?.Email}</span>
              </div>
              <div className="patient-info-item">
                <label>Peso (kg)</label>
                <span>{patientInfo?.Peso}</span>
              </div>
              <div className="patient-info-item">
                <label>Altura (cm)</label>
                <span>{patientInfo?.Altura}</span>
              </div>
              {patientInfo?.Peso && patientInfo?.Altura && parseFloat(patientInfo.Altura) > 0 && (
                <div className="patient-info-item">
                  <label>IMC</label>
                  <span>{(() => {
                    const peso = parseFloat(patientInfo.Peso);
                    const alturaM = parseFloat(patientInfo.Altura) / 100;
                    const imc = peso / (alturaM * alturaM);
                    return imc.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                  })()}</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="booking-selection">
          <h3>Consultas:</h3>
          {bookingsLoading ? (
            <div className="loading-container">
              <ClipLoader color="#4a90e2" size={35} />
            </div>
          ) : patientBookings.length === 0 ? (
            <p>Nenhuma consulta encontrada.</p>
          ) : (
            <div className="bookings-list">
              {patientBookings.map((booking, index) => (
                <div 
                  key={booking.atendimento_id || index} 
                  className={`booking-item ${selectedBooking?.atendimento_id === booking.atendimento_id ? 'active' : ''}`}
                  onClick={() => handleBookingSelect(booking)}
                >
                  <div className="booking-date">
                    <FaCalendarAlt style={{ marginRight: '5px' }} />
                    {new Date(booking.datetimeAtendimento).toLocaleDateString()} às {new Date(booking.datetimeAtendimento).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                  <div className="booking-professional">
                    {booking.professional}
                  </div>
                  <div className="booking-procedures">
                    {booking.procedimentos?.map(proc => proc.nome).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedBooking && (
          <>
            <div className="form-row">
              <div className="form-group">
                <div className="field-header">
                  <label>Prontuário Médico:</label>
                  {renderTranscriptionButton('medical')}
                </div>
                <select onChange={handleMedicalTemplateSelect} defaultValue="">
                  <option value="" disabled>Inserir modelo...</option>
                  {medicalTemplates.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <textarea 
                  value={medicalRecord} 
                  onChange={(e) => setMedicalRecord(e.target.value)}
                  placeholder="Registre aqui as informações médicas do paciente..."
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <div className="field-header">
                  <label>Receita:</label>
                  {renderTranscriptionButton('prescription')}
                </div>
                <select onChange={handlePrescriptionTemplateSelect} defaultValue="">
                  <option value="" disabled>Inserir modelo...</option>
                  {prescriptionTemplates.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <textarea 
                  value={prescription} 
                  onChange={(e) => setPrescription(e.target.value)}
                  placeholder="Registre aqui a receita médica para impressão..."
                />
              </div>
            </div>

            <div className="prescription-history">
              <div className="prescription-history-header">
                <h3>Histórico de Receitas</h3>
                <span>{prescriptionHistory.length} salva{prescriptionHistory.length === 1 ? '' : 's'}</span>
              </div>
              {prescriptionHistory.length === 0 ? (
                <p className="prescription-history-empty">Nenhuma receita salva para esta consulta.</p>
              ) : (
                <div className="prescription-history-list">
                  {prescriptionHistory.map((snapshot, index) => (
                    <div className="prescription-history-item" key={snapshot.id || index}>
                      <div className="prescription-history-meta">
                        <strong>
                          Receita {prescriptionHistory.length - index}
                        </strong>
                        <span>
                          {new Date(snapshot.printed_at || snapshot.created_at).toLocaleString('pt-BR', {
                            dateStyle: 'short',
                            timeStyle: 'short'
                          })}
                        </span>
                      </div>
                      <pre>{snapshot.content}</pre>
                      <button
                        className="button button-secondary"
                        onClick={() => handleReprintPrescription(snapshot)}
                      >
                        <FaPrint /> Reimprimir
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="button-container">
              <button 
                className="button button-secondary"
                onClick={() => setManageOpen(true)}
                disabled={templatesLoading}
              >
                <FaPencilAlt/> Gerenciar Modelos de texto
              </button>
              <button 
                className="button button-secondary"
                onClick={handlePrintProntuario}
                disabled={loading || !medicalRecord}
              >
                <FaPrint /> Imprimir Prontuário
              </button>
              <button 
                className="button button-secondary"
                onClick={handlePrint}
                disabled={loading || !prescription}
              >
                <FaPrint /> Imprimir Receita
              </button>
              <button
                className="button button-secondary"
                onClick={handleNewPrescription}
                disabled={loading}
              >
                <FaPlus /> Nova Receita
              </button>
              <button 
                className="button button-primary"
                onClick={saveChanges}
                disabled={loading}
              >
                {loading ? (
                  <><ClipLoader color="#ffffff" size={14} /> Salvando...</>
                ) : (
                  <><FaSave /> Salvar</>
                )}
              </button>
            {/* <div className="button-gro">
              <div className="form-group"> */}
              {/* </div>
            </div> */}
            </div>

            {(saveStatus.message || autoSaveStatus.message) && (
              <div className={`save-status ${saveStatus.type || autoSaveStatus.type}`}>
                {saveStatus.message || autoSaveStatus.message}
              </div>
            )}


            {manageOpen && (
              <TemplatesModal
                showModal={manageOpen}
                onClose={() => setManageOpen(false)}
                medicalTemplates={medicalTemplates}
                prescriptionTemplates={prescriptionTemplates}
                newType={newType}
                setNewType={setNewType}
                newLabel={newLabel}
                setNewLabel={setNewLabel}
                newText={newText}
                setNewText={setNewText}
                editLabel={editLabel}
                setEditLabel={setEditLabel}
                editText={editText}
                setEditText={setEditText}
                editing={editing}
                addTemplate={addTemplate}
                startEdit={startEdit}
                saveEdit={saveEdit}
                deleteTemplate={deleteTemplate}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ViewProntuario; 
